'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { AlertCircle, Pencil, ShoppingCart } from 'lucide-react';
import { AutoGrowTextarea } from './AutoGrowTextarea';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { bookRequestHeaders } from '@/components/manga/request-headers';
import { DEDICATION_MAX_CHARS } from '@/lib/books/dedication';
import {
  NOT_EDITABLE_MESSAGE,
  STORY_SLOW_MESSAGE,
  checkoutHref,
  dedicationCount,
  editHintText,
  storyPollExpired,
  withBookToken,
} from './flow-state';

const POLL_MS = 3000;
const BUSY_TRIES = 5;
const WRITING = 'Writing your story… this takes about a minute';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type StoryState = 'loading' | 'writing' | 'ready' | 'failed' | 'lost' | 'offline' | 'slow';

interface StoryPage {
  pageNumber: number;
  text: string;
  /** The text the server holds (what a blur compares against). */
  saved: string;
  /** The story's own text, when known (the page was unedited when it loaded): an edit back to it is not an edit. */
  original: string | null;
  edited: boolean;
}

type PutResult = { ok: true; data: any } | { ok: false; status: number; data: any };

interface PictureBookStoryStepProps {
  bookId: string;
  /** An emailed link's ?bt= token, carried onto every book request. */
  accessToken: string;
  /** Whether any page now differs from the story as written (owner FLAG 1 locks the preview controls). */
  onEdited: (edited: boolean) => void;
  onStartNewBook: () => void;
  onBack: () => void;
}

/**
 * Read your story: the page texts (each editable, saved on blur with gentle hints), the dedication, then checkout.
 * Polls GET picture-book-story every 3 s while the story is written. The server decides what may still be edited
 * (P2-R9: only a paid or in-process order fixes the text).
 */
export function PictureBookStoryStep({ bookId, accessToken, onEdited, onStartNewBook, onBack }: PictureBookStoryStepProps) {
  const router = useRouter();
  const [state, setState] = useState<StoryState>('loading');
  const [pages, setPages] = useState<StoryPage[]>([]);
  const [dedication, setDedication] = useState('');
  const [savedDedication, setSavedDedication] = useState('');
  const [hints, setHints] = useState<Record<number, string[]>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notEditable, setNotEditable] = useState(false);
  const [saving, setSaving] = useState(0);
  // Try again after the poll deadline starts a new poll run (with a fresh deadline)
  const [pollRun, setPollRun] = useState(0);

  const cancelledRef = useRef(false);
  const savingRef = useRef<Promise<unknown>>(Promise.resolve());
  const pagesRef = useRef<StoryPage[]>([]);
  pagesRef.current = pages;
  // Kept in step with `errors` synchronously, so checkout (after awaiting the saves) sees a refused save
  const errorsRef = useRef<Record<string, string>>({});
  const setError = (key: string, text: string | null) => {
    const { [key]: _old, ...rest } = errorsRef.current;
    errorsRef.current = text ? { ...rest, [key]: text } : rest;
    setErrors(errorsRef.current);
  };

  const storyUrl = withBookToken(`/api/books/${encodeURIComponent(bookId)}/picture-book-story`, accessToken);

  useEffect(() => {
    cancelledRef.current = false;
    // Per run: a re-run (React dev double effect) never leaves two polls going
    let stopped = false;
    const done = () => stopped || cancelledRef.current;
    let failures = 0;
    const startedAt = Date.now();
    const poll = async () => {
      const headers = await bookRequestHeaders();
      for (;;) {
        if (done()) return;
        try {
          const res = await fetch(storyUrl, { headers, cache: 'no-store' });
          if (res.status === 403 || res.status === 404) {
            setState('lost');
            return;
          }
          if (res.ok) {
            failures = 0;
            const data = await res.json();
            if (done()) return;
            if (data?.state === 'ready') {
              const loaded: StoryPage[] = (Array.isArray(data.pages) ? data.pages : []).map((p: any) => {
                const text = typeof p?.text === 'string' ? p.text : '';
                return { pageNumber: Number(p?.pageNumber), text, saved: text, original: p?.edited ? null : text, edited: !!p?.edited };
              });
              const d = typeof data.dedication === 'string' ? data.dedication : '';
              setPages(loaded);
              setDedication(d);
              setSavedDedication(d);
              setState('ready');
              onEdited(loaded.some((p) => p.edited));
              return;
            }
            if (data?.state === 'failed') {
              setState('failed');
              return;
            }
            setState('writing');
          } else if (++failures >= 3) {
            setState('offline'); // keeps polling: recovers on the next good answer
          }
        } catch {
          if (++failures >= 3) setState('offline');
        }
        // No endless polling (each GET may start the page script): stop and offer Try again
        if (storyPollExpired(startedAt, Date.now())) {
          setState('slow');
          return;
        }
        await sleep(POLL_MS);
      }
    };
    void poll();
    return () => {
      stopped = true;
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, pollRun]);

  /** PUT one edit; a busy lock (another change is being written) is retried. */
  const put = async (body: Record<string, unknown>): Promise<PutResult> => {
    const headers = await bookRequestHeaders();
    for (let attempt = 0; attempt < BUSY_TRIES; attempt++) {
      let res: Response;
      let data: any;
      try {
        res = await fetch(storyUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify(body),
        });
        data = await res.json().catch(() => ({}));
      } catch {
        return { ok: false, status: 0, data: {} };
      }
      if (res.ok) return { ok: true, data };
      if (res.status === 409 && data?.error === 'busy') {
        await sleep(Math.max(1000, Number(data?.retryAfterMs) || 5000));
        if (cancelledRef.current) return { ok: false, status: 0, data: {} };
        continue;
      }
      return { ok: false, status: res.status, data };
    }
    return { ok: false, status: 409, data: { error: 'busy' } };
  };

  /** Saves run one after another, so checkout waits for all of them. */
  const queue = (job: () => Promise<void>) => {
    setSaving((n) => n + 1);
    savingRef.current = savingRef.current.catch(() => undefined).then(job).finally(() => setSaving((n) => n - 1));
  };

  const failed = (key: string, r: { status: number; data: any }) => {
    if (r.status === 409 && r.data?.error === 'not-editable') {
      setNotEditable(true);
      return;
    }
    const text = r.status === 400 && typeof r.data?.error === 'string' ? r.data.error : "We couldn't save this. Please try again.";
    setError(key, text);
  };

  const savePage = (pageNumber: number) => {
    const page = pagesRef.current.find((p) => p.pageNumber === pageNumber);
    if (!page || notEditable || page.text.trim() === page.saved.trim()) return;
    const text = page.text;
    queue(async () => {
      const r = await put({ pages: [{ pageNumber, text }] });
      if (cancelledRef.current) return;
      if ('status' in r) {
        failed(`page-${pageNumber}`, r);
        return;
      }
      setError(`page-${pageNumber}`, null);
      const notes = (Array.isArray(r.data?.hints) ? r.data.hints : [])
        .filter((h: any) => Number(h?.pageNumber) === pageNumber)
        .map((h: any) => editHintText({ kind: String(h?.kind ?? ''), detail: String(h?.detail ?? '') }))
        .filter((t: string | null): t is string => !!t);
      setHints((h) => ({ ...h, [pageNumber]: notes }));
      const next = pagesRef.current.map((p) =>
        p.pageNumber === pageNumber
          ? { ...p, saved: text, edited: p.original === null ? true : text.trim() !== p.original.trim() }
          : p,
      );
      setPages(next);
      onEdited(next.some((p) => p.edited));
    });
  };

  const saveDedication = () => {
    if (notEditable || dedication.trim() === savedDedication.trim()) return;
    const value = dedication;
    queue(async () => {
      const r = await put({ dedication: value.trim() ? value : null });
      if (cancelledRef.current) return;
      if ('status' in r) {
        failed('dedication', r);
        return;
      }
      setError('dedication', null);
      setSavedDedication(typeof r.data?.dedication === 'string' ? r.data.dedication : '');
    });
  };

  const goToCheckout = async () => {
    await savingRef.current.catch(() => undefined);
    // A refused save keeps the parent here: the book must print what they see
    if (cancelledRef.current || Object.keys(errorsRef.current).length > 0) return;
    router.push(checkoutHref(bookId, accessToken));
  };

  const hasErrors = Object.keys(errors).length > 0;

  if (state === 'failed' || state === 'lost') {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-10 px-6 text-center space-y-4">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto" aria-hidden />
          <p className="text-sm text-red-700 max-w-md mx-auto">
            {state === 'failed'
              ? "We couldn't write this story. Please start a new book."
              : 'This book link is no longer valid in this browser. Please start a new book.'}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 pt-2">
            <Button variant="outline" onClick={onBack}>
              Back
            </Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={onStartNewBook}>
              Start a new book
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-1">
        <h2 className="text-2xl font-bold">Read your story</h2>
        {state === 'ready' && (
          <p className="text-gray-600 max-w-xl mx-auto">Change any words you like. Your book is printed with the words you see here.</p>
        )}
      </div>

      {state !== 'ready' ? (
        <Card>
          <CardContent className="py-12 px-6 text-center space-y-4">
            <div className="relative w-12 h-12 mx-auto">
              <div className="absolute inset-0 rounded-full border-4 border-purple-100" />
              <div className="absolute inset-0 rounded-full border-4 border-purple-600 border-t-transparent animate-spin" />
              <Pencil className="absolute inset-0 m-auto w-5 h-5 text-purple-600" aria-hidden />
            </div>
            <p className="text-purple-700 font-medium" aria-live="polite">
              {state === 'slow'
                ? STORY_SLOW_MESSAGE
                : state === 'offline'
                  ? 'We lost touch with your story. Please check your connection; we keep trying.'
                  : WRITING}
            </p>
            {state === 'slow' && (
              <Button
                variant="outline"
                onClick={() => {
                  setState('loading');
                  setPollRun((n) => n + 1);
                }}
              >
                Try again
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {notEditable && <p className="text-sm text-gray-700 bg-gray-50 border rounded-md px-3 py-2">{NOT_EDITABLE_MESSAGE}</p>}
          <ol className="space-y-4">
            {pages.map((page) => (
              <li key={page.pageNumber}>
                <label className="block text-sm font-semibold text-gray-700" htmlFor={`pb-page-${page.pageNumber}`}>
                  Page {page.pageNumber}
                </label>
                <AutoGrowTextarea
                  id={`pb-page-${page.pageNumber}`}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900"
                  rows={3}
                  maxLength={1500}
                  value={page.text}
                  disabled={notEditable}
                  onChange={(e) => {
                    const text = e.target.value;
                    setPages((ps) => ps.map((p) => (p.pageNumber === page.pageNumber ? { ...p, text } : p)));
                  }}
                  onBlur={() => savePage(page.pageNumber)}
                />
                {(hints[page.pageNumber] ?? []).map((note, i) => (
                  <p key={`${i}-${note}`} className="text-sm text-amber-800 mt-1">
                    {note}
                  </p>
                ))}
                {errors[`page-${page.pageNumber}`] && <p className="text-sm text-red-600 mt-1">{errors[`page-${page.pageNumber}`]}</p>}
              </li>
            ))}
          </ol>

          <div>
            <label className="block text-sm font-semibold text-gray-700" htmlFor="pb-dedication">
              Dedication (optional)
            </label>
            <AutoGrowTextarea
              id="pb-dedication"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900"
              rows={3}
              maxLength={DEDICATION_MAX_CHARS}
              placeholder="For Mia, with love from Grandma"
              value={dedication}
              disabled={notEditable}
              onChange={(e) => setDedication(e.target.value)}
              onBlur={saveDedication}
            />
            <p className="text-xs text-gray-500 mt-1">
              {dedicationCount(dedication)}/{DEDICATION_MAX_CHARS}
            </p>
            {errors.dedication && <p className="text-sm text-red-600 mt-1">{errors.dedication}</p>}
          </div>
        </>
      )}

      {saving > 0 && <p className="text-xs text-gray-500 text-right">Saving…</p>}
      <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
        <Button variant="outline" size="lg" onClick={onBack} className="w-full sm:w-auto">
          Back
        </Button>
        <Button
          size="lg"
          className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto"
          disabled={state !== 'ready' || hasErrors}
          onClick={() => void goToCheckout()}
        >
          <ShoppingCart className="w-4 h-4 mr-2" aria-hidden />
          Continue to checkout
        </Button>
      </div>
    </div>
  );
}
